import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Input, Button, VStack, Text, HStack } from '@chakra-ui/react';
import { extensiveSearchHospitals } from '../../../Http/Index';
import { HospitalSearchResult } from '../types';
import '../styles/HospitalSimilarity.css';

const SEARCH_DEBOUNCE_DELAY = 500;
const MAX_RESULTS_HEIGHT = '200px';

interface InlineSearchProps {
  readonly hospitalId: string;
  readonly hospitalStatus: string;
  readonly selectedMatchId?: string;
  readonly onSelectMatch?: (hospitalId: string, matchId: string) => void;
}



const InlineSearch: React.FC<InlineSearchProps> = ({ hospitalId, hospitalStatus, selectedMatchId, onSelectMatch }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<HospitalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }

    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await extensiveSearchHospitals({
        searchInput: term.trim(),
        limit: 20
      }, abortControllerRef.current);
      setResults(response.data.data || []);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        console.error('Hospital search failed:', error);
      }
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timeoutId);
      // Abort any pending request when component unmounts or searchTerm changes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchTerm, handleSearch]);

  return (
    <Box mt={4} p={4} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
      <HStack mb={3}>
        <Input
          placeholder="Search main database..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="sm"
        />
        <Button size="sm" colorScheme="purple" onClick={() => handleSearch(searchTerm)} isLoading={loading}>
          🔍
        </Button>
      </HStack>

      {loading && searchTerm && (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
          Searching...
        </Text>
      )}

      {!loading && results.length > 0 && (
        <VStack spacing={2} align="stretch" maxH={MAX_RESULTS_HEIGHT} overflowY="auto">
          <Text fontSize="sm" fontWeight="semibold" color="gray.700">
            Found {results.length} hospital(s):
          </Text>
          {results.map((hospital, index) => (
            <Box key={hospital._id || hospital.id} bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
              <HStack align="flex-start">
                {(hospitalStatus === 'doubtful' || hospitalStatus === 'new_hospital') && (hospital._id || hospital.id) && (
                  <input
                    type="radio"
                    name={`hospital-select-${hospitalId}`}
                    checked={selectedMatchId === (hospital._id || hospital.id)}
                    onChange={() => {
                      try {
                        const matchId = hospital._id || hospital.id;
                        if (matchId && onSelectMatch) {
                          onSelectMatch(hospitalId, matchId);
                        }
                      } catch (error) {
                        console.error('Failed to select match:', error);
                      }
                    }}
                    className="radio-input"
                  />
                )}
                <VStack align="flex-start" spacing={1} flex={1}>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    {hospital.formattedData?.name || hospital.name}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    {hospital.formattedData?.address || hospital.address}
                  </Text>
                  <HStack spacing={4} fontSize="xs" color="gray.500">
                    <Text>ID: {hospital._id}</Text>
                    <Text>City: {hospital.formattedData?.city}</Text>
                    <Text>Pincode: {hospital.formattedData?.pincode}</Text>
                  </HStack>
                </VStack>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}

      {!loading && searchTerm && results.length === 0 && (
        <Text fontSize="sm" color="gray.500" fontStyle="italic" textAlign="center" py={4}>
          No hospitals found for "{searchTerm}"
        </Text>
      )}
    </Box>
  );
};

export default InlineSearch;